import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Palette, Crown, Check, Code2, LayoutTemplate, ImagePlus, Save, RotateCcw, Undo2, Lock, KeyRound, Copy, Monitor, Tablet, Smartphone } from 'lucide-react';
import PlansModal from './PlansModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONTS = [
  { id: 'outfit', name: 'Outfit', style: "'Outfit', sans-serif" },
  { id: 'figtree', name: 'Figtree', style: "'Figtree', sans-serif" },
  { id: 'inter', name: 'Inter', style: "'Inter', sans-serif" },
  { id: 'playfair', name: 'Playfair Display', style: "'Playfair Display', serif" },
  { id: 'merriweather', name: 'Merriweather', style: "'Merriweather', serif" },
  { id: 'montserrat', name: 'Montserrat', style: "'Montserrat', sans-serif" },
];

const HERO_STYLES = [
  { id: 'centered', lv: 'Centrēts', en: 'Centered' },
  { id: 'left', lv: 'Kreisais', en: 'Left-aligned' },
  { id: 'minimal', lv: 'Minimāls', en: 'Minimal' },
];
const PRODUCT_LAYOUTS = [
  { id: 'grid', lv: 'Režģis', en: 'Grid' },
  { id: 'list', lv: 'Saraksts', en: 'List' },
];

const COLOR_PRESETS = [
  { name: 'KoLV', primary: '#4A6B53', secondary: '#D97736', bg: '#FAFAFA', text: '#121212' },
  { name: 'Latvian', primary: '#9E1B32', secondary: '#ffffff', bg: '#fff5f5', text: '#1a1a1a' },
  { name: 'Light', primary: '#0f172a', secondary: '#2563eb', bg: '#ffffff', text: '#0f172a' },
  { name: 'Dark', primary: '#e2e8f0', secondary: '#f59e0b', bg: '#0f172a', text: '#e2e8f0' },
  { name: 'Ocean', primary: '#1e40af', secondary: '#f59e0b', bg: '#f8fafc', text: '#0f172a' },
  { name: 'Berry', primary: '#7c3aed', secondary: '#ec4899', bg: '#faf5ff', text: '#1e1b2e' },
  { name: 'Earth', primary: '#92400e', secondary: '#059669', bg: '#fffbeb', text: '#1c1917' },
  { name: 'Premium', primary: '#111111', secondary: '#c9a227', bg: '#f7f7f5', text: '#111111' },
];

const TEMPLATES = [
  { id: 'minimalist', lv: 'Minimālisms', en: 'Minimalist', desc: { lv: 'Balts, tīrs, Apple stils', en: 'White, clean, Apple-style' } },
  { id: 'bold', lv: 'Drosmīgs', en: 'Bold', desc: { lv: 'Lieli fonti, kontrasti', en: 'Big fonts, contrast' } },
  { id: 'vintage', lv: 'Vintage', en: 'Vintage', desc: { lv: 'Silti toņi, retro', en: 'Warm tones, retro' } },
  { id: 'craft', lv: 'Amatniecība', en: 'Craft/Local', desc: { lv: 'Latviski, dabīgi', en: 'Latvian, natural' } },
  { id: 'modern', lv: 'Modern Commerce', en: 'Modern Commerce', desc: { lv: 'Shopify/Amazon stils', en: 'Shopify/Amazon vibe' } },
];

const DEFAULTS = {
  primary_color: '#4A6B53', secondary_color: '#D97736', bg_color: '#FAFAFA', text_color: '#121212',
  font_family: 'outfit', hero_style: 'centered', product_layout: 'grid',
  custom_css: '', template: '', banner_top_html: '', banner_bottom_html: '', apply_to_profile: false, liquid_theme: '', theme_lang: 'liquid',
};

const PLAN_CARDS = [
  { id: 'free', price: 0, name: 'Bezmaksas', feats: { lv: ['Veikals + sludinājumi (līdz 5 bildēm)', 'Noklusējuma dizains'], en: ['Shop + listings (up to 5 images)', 'Default design'] } },
  { id: 'basic', price: 9, name: 'Basic', feats: { lv: ['Krāsu paletes + fonti', 'Hero & izkārtojums', 'Custom CSS (drošs)', 'Līdz 12 bildēm sludinājumā'], en: ['Color palettes + fonts', 'Hero & layout', 'Custom CSS (sandboxed)', 'Up to 12 images/listing'] } },
  { id: 'premium', price: 29, name: 'Premium', popular: true, feats: { lv: ['Viss no Basic', '5 gatavie šabloni', 'HTML banneri (droši)', 'Līdz 20 bildēm sludinājumā'], en: ['Everything in Basic', '5 premium templates', 'HTML banners (safe)', 'Up to 20 images/listing'] } },
  { id: 'premium_max', price: 49, name: 'Premium Max', feats: { lv: ['Viss no Premium', 'Preview / Reset / Undo', 'Arī publiskajam profilam', 'Līdz 25 bildēm sludinājumā'], en: ['Everything in Premium', 'Preview / Reset / Undo', 'Also on public profile', 'Up to 25 images/listing'] } },
  { id: 'premium_ultra', price: 99, name: 'Premium Ultra', feats: { lv: ['Viss no Premium Max', 'Headless API piekļuve', 'Tēmu dzinējs (Liquid / Jinja2)', 'Webhooks + piegādes žurnāls', 'Līdz 30 bildēm sludinājumā'], en: ['Everything in Premium Max', 'Headless API access', 'Theme engine (Liquid / Jinja2)', 'Webhooks + delivery log', 'Up to 30 images/listing'] } },
];

const THEME_GALLERY = [
  {
    id: 'gallery-grid', name: { lv: 'Režģa veikals', en: 'Grid shop' },
    liquid: `<style>
  body{margin:0;font-family:sans-serif;color:#121212}
  .hd{padding:40px 24px;background:#4A6B53;color:#fff;text-align:center}
  .hd h1{margin:0;font-size:32px}
  .grid{max-width:1000px;margin:24px auto;padding:0 24px;display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
  .card{border:1px solid #eee;border-radius:12px;overflow:hidden}
  .card img{width:100%;height:170px;object-fit:cover}
  .card .b{padding:12px}
  .price{color:#D97736;font-weight:700}
</style>
<div class="hd"><h1>{{ shop.name }}</h1><p>{{ shop.description }}</p></div>
<div class="grid">
  {% for l in listings %}
  <div class="card">
    {% if l.image %}<img src="{{ l.image }}" alt="{{ l.title }}">{% endif %}
    <div class="b"><strong>{{ l.title }}</strong><div class="price">{{ l.price_formatted }}</div></div>
  </div>
  {% endfor %}
</div>`,
  },
  {
    id: 'gallery-hero', name: { lv: 'Hero banneris', en: 'Hero banner' },
    liquid: `<style>
  body{margin:0;font-family:Georgia,serif;color:#1a1a1a;background:#fff5f5}
  .hero{height:280px;display:flex;align-items:center;justify-content:center;flex-direction:column;background:#9E1B32;color:#fff;text-align:center}
  .hero h1{font-size:42px;margin:0}
  .list{max-width:760px;margin:30px auto;padding:0 24px}
  .row{display:flex;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee}
  .price{color:#9E1B32;font-weight:700}
</style>
<div class="hero"><h1>{{ shop.name }}</h1><p>{{ shop.listing_count }} {{ 'preces' }}</p></div>
<div class="list">
  {% for l in listings %}
  <div class="row"><span>{{ l.title }}</span><span class="price">{{ l.price_formatted }}</span></div>
  {% endfor %}
</div>`,
  },
  {
    id: 'gallery-minimal', name: { lv: 'Minimālisms', en: 'Minimalist' },
    liquid: `<style>
  body{margin:0;font-family:Helvetica,Arial,sans-serif;color:#111}
  .wrap{max-width:880px;margin:60px auto;padding:0 24px}
  h1{font-weight:300;letter-spacing:-1px;font-size:40px}
  .items{margin-top:40px}
  .it{display:flex;gap:16px;align-items:center;padding:16px 0;border-top:1px solid #eee}
  .it img{width:72px;height:72px;object-fit:cover;border-radius:8px}
  .price{margin-left:auto;font-weight:600}
</style>
<div class="wrap">
  <h1>{{ shop.name }}</h1>
  <div class="items">
    {% for l in listings %}
    <div class="it">{% if l.image %}<img src="{{ l.image }}">{% endif %}<span>{{ l.title }}</span><span class="price">{{ l.price_formatted }}</span></div>
    {% endfor %}
  </div>
</div>`,
  },
  {
    id: 'gallery-dark', name: { lv: 'Tumšs', en: 'Dark' },
    liquid: `<style>
  body{margin:0;font-family:'Segoe UI',sans-serif;background:#0f172a;color:#e2e8f0}
  .hd{padding:48px 24px;text-align:center;border-bottom:1px solid #1e293b}
  .hd h1{margin:0;font-size:36px;color:#fff}
  .hd p{color:#94a3b8}
  .grid{max-width:1040px;margin:28px auto;padding:0 24px;display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
  .card{background:#1e293b;border-radius:14px;overflow:hidden}
  .card img{width:100%;height:150px;object-fit:cover}
  .card .b{padding:12px}
  .price{color:#f59e0b;font-weight:700;margin-top:4px}
</style>
<div class="hd"><h1>{{ shop.name }}</h1><p>{{ shop.description }}</p></div>
<div class="grid">
  {% for l in listings %}
  <div class="card">{% if l.image %}<img src="{{ l.image }}">{% endif %}<div class="b"><strong>{{ l.title }}</strong><div class="price">{{ l.price_formatted }}</div></div></div>
  {% endfor %}
</div>`,
  },
  {
    id: 'gallery-magazine', name: { lv: 'Žurnāls', en: 'Magazine' },
    liquid: `<style>
  body{margin:0;font-family:Georgia,serif;color:#1a1a1a;background:#fffdf7}
  .top{max-width:1080px;margin:0 auto;padding:48px 24px 16px;border-bottom:3px solid #1a1a1a}
  .top h1{font-size:48px;margin:0;letter-spacing:-1px}
  .top span{text-transform:uppercase;letter-spacing:3px;font-size:12px;color:#888}
  .feature{max-width:1080px;margin:24px auto;padding:0 24px;display:grid;grid-template-columns:2fr 1fr;gap:24px}
  .feature .main img{width:100%;height:360px;object-fit:cover}
  .side .it{padding:12px 0;border-bottom:1px solid #eee}
  .price{color:#9E1B32;font-weight:700}
</style>
<div class="top"><span>KoLV · {{ shop.region }}</span><h1>{{ shop.name }}</h1></div>
<div class="feature">
  <div class="main">
    {% for l in listings limit:1 %}
      {% if l.image %}<img src="{{ l.image }}">{% endif %}
      <h2>{{ l.title }}</h2><p class="price">{{ l.price_formatted }}</p>
    {% endfor %}
  </div>
  <div class="side">
    {% for l in listings offset:1 limit:8 %}
      <div class="it"><strong>{{ l.title }}</strong> — <span class="price">{{ l.price_formatted }}</span></div>
    {% endfor %}
  </div>
</div>`,
  },
];

const JINJA_GALLERY = [
  {
    id: 'jinja-grid', name: { lv: 'Režģis (Jinja2)', en: 'Grid (Jinja2)' },
    liquid: `<style>
  body{margin:0;font-family:sans-serif;color:#121212}
  .hd{padding:40px 24px;background:#4A6B53;color:#fff;text-align:center}
  .hd h1{margin:0;font-size:32px}
  .grid{max-width:1000px;margin:24px auto;padding:0 24px;display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
  .card{border:1px solid #eee;border-radius:12px;overflow:hidden}
  .card img{width:100%;height:170px;object-fit:cover}
  .card .b{padding:12px}
  .num{font-size:11px;color:#888}
  .price{color:#D97736;font-weight:700}
</style>
<div class="hd"><h1>{{ shop.name }}</h1><p>{{ shop.description }}</p></div>
<div class="grid">
  {% for l in listings %}
  <div class="card">
    {% if l.image %}<img src="{{ l.image }}" alt="{{ l.title }}">{% endif %}
    <div class="b">
      <span class="num">#{{ loop.index }}</span>
      <strong>{{ l.title }}</strong>
      <div class="price">{{ l.price_formatted or "€%.2f"|format(l.price) }}</div>
    </div>
  </div>
  {% endfor %}
</div>`,
  },
  {
    id: 'jinja-stats', name: { lv: 'Ar statistiku (Jinja2)', en: 'With stats (Jinja2)' },
    liquid: `<style>
  body{margin:0;font-family:'Segoe UI',sans-serif;color:#111}
  .hd{padding:44px 24px;text-align:center;background:#0f172a;color:#fff}
  .hd h1{margin:0;font-size:34px}
  .bar{display:flex;gap:24px;justify-content:center;margin-top:14px;color:#94a3b8;font-size:14px}
  .bar b{color:#f59e0b}
  .wrap{max-width:920px;margin:30px auto;padding:0 24px}
  .it{display:flex;gap:14px;align-items:center;padding:14px 0;border-bottom:1px solid #eee}
  .it img{width:64px;height:64px;object-fit:cover;border-radius:8px}
  .price{margin-left:auto;font-weight:700;color:#4A6B53}
</style>
<div class="hd">
  <h1>{{ shop.name }}</h1>
  <div class="bar">
    <span>Preces: <b>{{ stats.listing_count }}</b></span>
    <span>Pārdots: <b>{{ stats.sales_count }}</b></span>
    <span>Vērtējums: <b>{{ stats.avg_rating | default('—') }}</b></span>
  </div>
</div>
<div class="wrap">
  {% for l in listings %}
  <div class="it">
    {% if l.image %}<img src="{{ l.image }}">{% endif %}
    <span>{{ loop.index }}. {{ l.title | truncate(48) }}</span>
    <span class="price">{{ l.price_formatted or "€%.2f"|format(l.price) }}</span>
  </div>
  {% else %}
  <p>Nav aktīvu sludinājumu.</p>
  {% endfor %}
</div>`,
  },
];

export default function PremiumCustomizer() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const tr = (lv, en) => (language === 'lv' ? lv : en);

  const [features, setFeatures] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [active, setActive] = useState(false);
  const [c, setC] = useState(DEFAULTS);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [hasDraft, setHasDraft] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [themePreview, setThemePreview] = useState(null);
  const [themeError, setThemeError] = useState(null);
  const [livePreview, setLivePreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('openPlans')) setShowPlans(true);
  }, []);
  const [promoActive, setPromoActive] = useState(false);
  useEffect(() => { axios.get(`${API}/premium/promo`).then(({ data }) => setPromoActive(!!data.active)).catch(() => {}); }, []);
  const [themeBusy, setThemeBusy] = useState(false);
  const [versions, setVersions] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookEvents, setWebhookEvents] = useState(['order.paid', 'order.shipped', 'inventory.low']);
  const [availableEvents, setAvailableEvents] = useState(['order.paid', 'order.shipped', 'inventory.low']);
  const [webhookPaused, setWebhookPaused] = useState(false);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deliveries, setDeliveries] = useState([]);
  const loaded = useRef(false);
  const themeLangRef = useRef('liquid');

  const has = (f) => features.includes(f);

  useEffect(() => { init(); }, []); // eslint-disable-line

  const init = async () => {
    try {
      const { data } = await axios.get(`${API}/premium/status`, { withCredentials: true });
      if (data.active) { setActive(true); setPlanId(data.plan_id); setFeatures(data.features || []); setExpiresAt(data.expires_at); }
      if (data.active && (data.features || []).includes('headless_api')) {
        try {
          const r = await axios.get(`${API}/premium/api-key`, { withCredentials: true });
          setApiKey(r.data.api_key || null);
        } catch (e) {}
        try {
          const w = await axios.get(`${API}/premium/webhook`, { withCredentials: true });
          setWebhookUrl(w.data.webhook_url || ''); setWebhookSecret(w.data.webhook_secret || '');
          if (w.data.available_events) setAvailableEvents(w.data.available_events);
          if (w.data.webhook_events) setWebhookEvents(w.data.webhook_events);
          setWebhookPaused(!!w.data.webhook_paused);
        } catch (e) {}
        try {
          const dlv = await axios.get(`${API}/premium/webhook/deliveries`, { withCredentials: true });
          setDeliveries(dlv.data.deliveries || []);
        } catch (e) {}
      }
      if (data.active && (data.features || []).includes('theme_engine')) {
        try {
          const v = await axios.get(`${API}/shop-customization/theme/versions`, { withCredentials: true });
          setVersions(v.data.versions || []);
        } catch (e) {}
      }
    } catch (e) {}
    try {
      const { data } = await axios.get(`${API}/shop-customization/me`, { withCredentials: true });
      if (data && Object.keys(data).length) {
        const { draft, updated_at, user_id, ...published } = data; // eslint-disable-line
        if (draft) {
          const { updated_at: _u, ...d } = draft; // eslint-disable-line
          setC({ ...DEFAULTS, ...published, ...d });
          setHasDraft(true);
          themeLangRef.current = d.theme_lang || published.theme_lang || 'liquid';
        } else {
          setC({ ...DEFAULTS, ...published });
          themeLangRef.current = published.theme_lang || 'liquid';
        }
      }
    } catch (e) {}
    try {
      const { data } = await axios.get(`${API}/wallet`, { withCredentials: true });
      setWalletBalance(data.balance || 0);
    } catch (e) {}
    // Carry over the accent color chosen on the Premium Demo page (?theme=RRGGBB).
    const themeParam = new URLSearchParams(window.location.search).get('theme');
    if (themeParam && /^[0-9a-fA-F]{6}$/.test(themeParam)) {
      setC((prev) => ({ ...prev, primary_color: `#${themeParam}` }));
    }
    loaded.current = true;
  };

  const update = (patch) => {
    setHistory((h) => [...h.slice(-19), c]);
    setC((prev) => ({ ...prev, ...patch }));
  };
  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      setC(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };
  const resetDefaults = () => { setHistory((h) => [...h, c]); setC(DEFAULTS); };

  const handleSubscribe = async (plan) => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/premium/subscribe`, { plan_id: plan, origin_url: window.location.origin }, { withCredentials: true });
      if (data.free) { alert(data.message || 'Aktivizēts bez maksas (launch akcija)!'); window.location.reload(); return; }
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(formatApiError(e, 'Kļūda')); } finally { setSubscribing(false); }
  };
  const handleWalletSubscribe = async (plan) => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/wallet/pay`, { type: 'premium', plan_id: plan }, { withCredentials: true });
      alert(data.message || 'Premium aktivizēts!');
      window.location.reload();
    } catch (e) { alert(formatApiError(e, 'Kļūda')); } finally { setSubscribing(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shop-customization`, c, { withCredentials: true });
      setHasDraft(false);
      setSuccess(true); setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      alert(formatApiError(e, tr('Nepieciešams aktīvs Premium abonements', 'Active Premium required')));
    } finally { setSaving(false); }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shop-customization?draft=true`, c, { withCredentials: true });
      setHasDraft(true);
      alert(tr('Melnraksts saglabāts! Tas vēl nav redzams pircējiem.', 'Draft saved! Not visible to buyers yet.'));
    } catch (e) {
      alert(formatApiError(e, tr('Kļūda', 'Error')));
    } finally { setSaving(false); }
  };

  const fontStyle = FONTS.find((f) => f.id === c.font_family)?.style;

  const genApiKey = async () => {
    try {
      const { data } = await axios.post(`${API}/premium/api-key`, {}, { withCredentials: true });
      setApiKey(data.api_key);
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const regenApiKey = async () => {
    if (!window.confirm(tr('Ģenerēt jaunu atslēgu? Vecā pārstās darboties.', 'Generate a new key? The old one will stop working.'))) return;
    try {
      const { data } = await axios.post(`${API}/premium/api-key/regenerate`, {}, { withCredentials: true });
      setApiKey(data.api_key);
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const copyKey = () => { if (apiKey) navigator.clipboard?.writeText(apiKey); };

  const restoreVersion = async (idx) => {
    try {
      const { data } = await axios.post(`${API}/shop-customization/theme/versions/${idx}/restore`, {}, { withCredentials: true });
      update({ liquid_theme: data.liquid_theme }); setThemePreview(null);
      const v = await axios.get(`${API}/shop-customization/theme/versions`, { withCredentials: true });
      setVersions(v.data.versions || []);
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const saveWebhook = async () => {
    try {
      const { data } = await axios.put(`${API}/premium/webhook`, { webhook_url: webhookUrl, webhook_events: webhookEvents }, { withCredentials: true });
      setWebhookUrl(data.webhook_url || ''); setWebhookSecret(data.webhook_secret || '');
      if (data.webhook_events) setWebhookEvents(data.webhook_events);
      alert(tr('Webhook saglabāts', 'Webhook saved'));
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const toggleEvent = (ev) => {
    setWebhookEvents((prev) => prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]);
  };
  const reactivateWebhook = async () => {
    try {
      await axios.post(`${API}/premium/webhook/reactivate`, {}, { withCredentials: true });
      setWebhookPaused(false);
      alert(tr('Webhook atkārtoti aktivizēts', 'Webhook reactivated'));
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const testWebhook = async () => {
    try { await axios.post(`${API}/premium/webhook/test`, {}, { withCredentials: true }); alert(tr('Testa notikums nosūtīts', 'Test event sent')); setTimeout(loadDeliveries, 1500); }
    catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const loadDeliveries = async (ev = filterEvent, st = filterStatus) => {
    try {
      const params = {};
      if (ev) params.event = ev;
      if (st) params.status = st;
      const { data } = await axios.get(`${API}/premium/webhook/deliveries`, { params, withCredentials: true });
      setDeliveries(data.deliveries || []);
    } catch (e) {}
  };
  const exportDeliveries = async () => {
    try {
      const params = {};
      if (filterEvent) params.event = filterEvent;
      if (filterStatus) params.status = filterStatus;
      const res = await axios.get(`${API}/premium/webhook/deliveries/export`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'webhook-deliveries.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const retryDelivery = async (id) => {
    try { await axios.post(`${API}/premium/webhook/deliveries/${id}/retry`, {}, { withCredentials: true }); setTimeout(loadDeliveries, 1500); }
    catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };

  const exportTheme = () => {
    const blob = new Blob([c.liquid_theme || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'kolv-theme.liquid'; a.click();
    URL.revokeObjectURL(url);
  };
  const importTheme = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { update({ liquid_theme: String(ev.target.result || '').slice(0, 20000) }); setThemePreview(null); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const loadDefaultTheme = async () => {
    try {
      const lang = themeLangRef.current || c.theme_lang || 'liquid';
      const { data } = await axios.get(`${API}/shop-customization/theme/default`, { params: { lang }, withCredentials: true });
      update({ liquid_theme: data.default_theme });
    } catch (e) { alert(formatApiError(e, 'Kļūda')); }
  };
  const previewTheme = async () => {
    setThemeBusy(true);
    try {
      const { data } = await axios.post(`${API}/shop-customization/theme/preview`, { liquid_theme: c.liquid_theme, theme_lang: c.theme_lang || 'liquid' }, { withCredentials: true });
      if (data.error) { setThemePreview(null); alert(tr('Tēmas kļūda: ', 'Theme error: ') + data.error); }
      else setThemePreview(data.html || '');
    } catch (e) { alert(formatApiError(e, 'Kļūda')); } finally { setThemeBusy(false); }
  };

  // Debounced live preview: auto-render the theme as the seller types.
  useEffect(() => {
    if (!livePreview || !has('theme_engine')) return;
    if (!c.liquid_theme) { setThemePreview(null); setThemeError(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/shop-customization/theme/preview`,
          { liquid_theme: c.liquid_theme, theme_lang: c.theme_lang || 'liquid' }, { withCredentials: true });
        if (data.error) { setThemeError(data.error); }
        else { setThemePreview(data.html || ''); setThemeError(null); }
      } catch (e) { /* silent while typing */ }
    }, 800);
    return () => clearTimeout(t);
  }, [c.liquid_theme, c.theme_lang, livePreview]); // eslint-disable-line

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="premium-customizer">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h3 className="font-outfit font-semibold text-lg text-[#121212] flex items-center gap-2">
          <Palette className="w-5 h-5 text-[#D97736]" /> {tr('Veikala dizains', 'Shop Design')}
        </h3>
        {active && (
          <div className="flex items-center gap-2">
            <Badge className="bg-[#D97736] text-white border-0"><Crown className="w-3 h-3 mr-1" /> {PLAN_CARDS.find(p => p.id === planId)?.name || planId}</Badge>
            {expiresAt && <span className="text-xs text-[#5C5C5C]">{tr('Derīgs līdz', 'Valid until')}: {new Date(expiresAt).toLocaleDateString('lv-LV')}</span>}
          </div>
        )}
      </div>

      {/* Plan picker — compact single-row strip + "See plans" opens full modal */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="plan-strip">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {PLAN_CARDS.map((p) => {
            const isCurrent = active ? planId === p.id : p.id === 'free';
            return (
              <button key={p.id} type="button" onClick={() => setShowPlans(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${isCurrent ? 'bg-[#4A6B53] text-white border-[#4A6B53]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'}`}
                data-testid={`plan-chip-${p.id}`}>
                {isCurrent && <Check className="w-3 h-3" />}
                <span className="font-medium">{p.id === 'free' ? tr('Sākuma', 'Starter') : p.name}</span>
                <span className={isCurrent ? 'text-white/80' : 'text-[#9CA3AF]'}>{p.price === 0 ? tr('· bez maksas', '· free') : `· €${p.price}`}</span>
              </button>
            );
          })}
        </div>
        <Button type="button" onClick={() => setShowPlans(true)} className="bg-[#D97736] hover:bg-[#C06528] text-white text-xs h-9 shrink-0" data-testid="open-plans-modal">
          {tr('Skatīt plānus', 'See plans')} →
        </Button>
      </div>

      <PlansModal
        open={showPlans}
        onOpenChange={setShowPlans}
        plans={PLAN_CARDS}
        active={active}
        planId={planId}
        walletBalance={walletBalance}
        subscribing={subscribing}
        onSubscribe={(id) => { setShowPlans(false); handleSubscribe(id); }}
        onWalletSubscribe={(id) => { setShowPlans(false); handleWalletSubscribe(id); }}
        language={language}
        promo={promoActive}
      />

      {!active ? (
        <p className="text-sm text-[#5C5C5C] text-center">{tr('Izvēlies plānu, lai atbloķētu dizaina rīkus.', 'Choose a plan to unlock design tools.')}</p>
      ) : (
        <div className="space-y-7" data-testid="customizer-controls">
          {/* Templates (Premium+) */}
          <Section icon={LayoutTemplate} title={tr('Dizaina šablons', 'Design template')} locked={!has('templates')} tr={tr}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="template-grid">
              {TEMPLATES.map((t) => (
                <button key={t.id} disabled={!has('templates')} onClick={() => update({ template: c.template === t.id ? '' : t.id })}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${c.template === t.id ? 'border-[#D97736] bg-[#D97736]/5' : 'border-[#E5E4E0] hover:border-[#D97736]/50'} disabled:opacity-50`}
                  data-testid={`template-${t.id}`}>
                  <p className="text-sm font-medium text-[#121212]">{t[language] || t.lv}</p>
                  <p className="text-[10px] text-[#5C5C5C] leading-tight mt-0.5">{t.desc[language === 'lv' ? 'lv' : 'en']}</p>
                </button>
              ))}
            </div>
          </Section>

          {/* Colors */}
          <Section icon={Palette} title={tr('Krāsas', 'Colors')} locked={!has('colors')} tr={tr}>
            <div className="flex flex-wrap gap-2 mb-3">
              {COLOR_PRESETS.map((preset) => (
                <button key={preset.name} disabled={!has('colors')}
                  onClick={() => update({ primary_color: preset.primary, secondary_color: preset.secondary, bg_color: preset.bg, text_color: preset.text })}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E4E0] hover:border-[#D97736] transition-colors disabled:opacity-50" data-testid={`preset-${preset.name.toLowerCase()}`}>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                  <span className="text-xs text-[#5C5C5C]">{preset.name}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: 'primary_color', label: tr('Galvenā', 'Primary') },
                { key: 'secondary_color', label: tr('Akcentu', 'Accent') },
                { key: 'bg_color', label: tr('Fons', 'Background') },
                { key: 'text_color', label: tr('Teksts', 'Text') },
              ].map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" disabled={!has('colors')} value={c[key]} onChange={(e) => update({ [key]: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input disabled={!has('colors')} value={c[key]} onChange={(e) => update({ [key]: e.target.value })} className="h-8 text-xs bg-[#FAFAFA] border-[#E5E4E0]" />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Fonts + hero + layout */}
          <Section icon={Palette} title={tr('Fonti un izkārtojums', 'Fonts & layout')} locked={!has('fonts')} tr={tr}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">{tr('Fonts', 'Font')}</Label>
                <Select value={c.font_family} onValueChange={(v) => update({ font_family: v })} disabled={!has('fonts')}>
                  <SelectTrigger className="h-9 bg-[#FAFAFA] border-[#E5E4E0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONTS.map((f) => <SelectItem key={f.id} value={f.id}><span style={{ fontFamily: f.style }}>{f.name}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr('Hero stils', 'Hero style')}</Label>
                <Select value={c.hero_style} onValueChange={(v) => update({ hero_style: v })} disabled={!has('hero_style')}>
                  <SelectTrigger className="h-9 bg-[#FAFAFA] border-[#E5E4E0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{HERO_STYLES.map((s) => <SelectItem key={s.id} value={s.id}>{s[language] || s.lv}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr('Produktu izkārtojums', 'Product layout')}</Label>
                <Select value={c.product_layout} onValueChange={(v) => update({ product_layout: v })} disabled={!has('layout')}>
                  <SelectTrigger className="h-9 bg-[#FAFAFA] border-[#E5E4E0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_LAYOUTS.map((l) => <SelectItem key={l.id} value={l.id}>{l[language] || l.lv}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          {/* Custom CSS (Basic+) */}
          <Section icon={Code2} title={tr('Pielāgots CSS', 'Custom CSS')} locked={!has('custom_css')} tr={tr}>
            <p className="text-xs text-[#5C5C5C] mb-2">{tr('Drošs CSS — automātiski tiek piemērots tikai tavam veikalam (#kolv-shop). <script>, @import un bīstami konstrukti tiek bloķēti.', 'Sandboxed CSS — auto-scoped to your shop (#kolv-shop). Scripts, @import and unsafe constructs are blocked.')}</p>
            <Textarea disabled={!has('custom_css')} value={c.custom_css} onChange={(e) => update({ custom_css: e.target.value })} rows={6}
              placeholder=".product-card { border-radius: 16px; }&#10;.cta-button { background: #111; }"
              className="font-mono text-xs bg-[#FAFAFA] border-[#E5E4E0]" data-testid="custom-css-input" />
          </Section>

          {/* HTML banners (Premium+) */}
          <Section icon={ImagePlus} title={tr('HTML banneri', 'HTML banners')} locked={!has('banners')} tr={tr}>
            <p className="text-xs text-[#5C5C5C] mb-2">{tr('Drošs HTML (bez <script>). Augšējais banneris virs hero, apakšējais zem kataloga.', 'Safe HTML (no <script>). Top banner above hero, bottom below catalog.')}</p>
            <Label className="text-xs">{tr('Augšējais banneris', 'Top banner')}</Label>
            <Textarea disabled={!has('banners')} value={c.banner_top_html} onChange={(e) => update({ banner_top_html: e.target.value })} rows={3}
              placeholder='<div style="padding:12px;text-align:center">Vasaras akcija -20%!</div>'
              className="font-mono text-xs bg-[#FAFAFA] border-[#E5E4E0] mb-3" data-testid="banner-top-input" />
            <Label className="text-xs">{tr('Apakšējais banneris', 'Bottom banner')}</Label>
            <Textarea disabled={!has('banners')} value={c.banner_bottom_html} onChange={(e) => update({ banner_bottom_html: e.target.value })} rows={3}
              placeholder='<div style="padding:12px;text-align:center">Seko mums!</div>'
              className="font-mono text-xs bg-[#FAFAFA] border-[#E5E4E0]" data-testid="banner-bottom-input" />
          </Section>

          {/* Apply to profile (Premium Max) */}
          {has('apply_to_profile') && (
            <label className="flex items-center gap-2 text-sm text-[#121212] cursor-pointer" data-testid="apply-to-profile-toggle">
              <input type="checkbox" checked={!!c.apply_to_profile} onChange={(e) => update({ apply_to_profile: e.target.checked })} className="w-4 h-4 accent-[#4A6B53]" />
              {tr('Piemērot dizainu arī manam publiskajam profilam', 'Also apply this design to my public profile')}
            </label>
          )}

          {/* Headless API (Premium Ultra) */}
          {has('headless_api') && (
            <Section icon={KeyRound} title={tr('Headless API', 'Headless API')} locked={false} tr={tr}>
              <p className="text-xs text-[#5C5C5C] mb-2">{tr('Veido savu veikala priekšpusi (Next.js/Vue/WordPress) un pievelc savus datus caur API. Lieto galveni X-API-Key.', 'Build your own storefront and pull your data via API. Use the X-API-Key header.')}</p>
              {apiKey ? (
                <div className="flex items-center gap-2" data-testid="api-key-row">
                  <code className="flex-1 text-xs bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg px-3 py-2 font-mono break-all" data-testid="api-key-value">{apiKey}</code>
                  <Button type="button" variant="outline" onClick={copyKey} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="copy-api-key"><Copy className="w-4 h-4" /></Button>
                  <Button type="button" variant="outline" onClick={regenApiKey} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="regen-api-key">{tr('Atjaunot', 'Regenerate')}</Button>
                </div>
              ) : (
                <Button type="button" onClick={genApiKey} className="bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="generate-api-key">
                  <KeyRound className="w-4 h-4 mr-1" /> {tr('Ģenerēt API atslēgu', 'Generate API key')}
                </Button>
              )}
              <div className="mt-3 text-xs text-[#5C5C5C] bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 font-mono space-y-1">
                <div>GET /api/public/v1/shop</div>
                <div>GET /api/public/v1/listings</div>
                <div>GET /api/public/v1/listings/{'{id}'}</div>
                <div className="text-[10px] text-[#9CA3AF]">Header: X-API-Key: &lt;{tr('tava atslēga', 'your key')}&gt;</div>
              </div>
              <a href="/developers" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-[#D97736] underline" data-testid="api-docs-link">{tr('Skatīt API dokumentāciju →', 'View API docs →')}</a>
              <div className="mt-4 pt-4 border-t border-[#F3F2EE]">
                <p className="text-xs font-medium text-[#121212] mb-1.5">{tr('Webhook (jauns pasūtījums)', 'Webhook (new order)')}</p>
                <p className="text-[11px] text-[#5C5C5C] mb-2">{tr('KoLV nosūtīs parakstītu order.paid notikumu uz šo URL (galvene X-KoLV-Signature).', 'KoLV POSTs a signed order.paid event to this URL (header X-KoLV-Signature).')}</p>
                {webhookPaused && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2" data-testid="webhook-paused-banner">
                    <span className="text-[11px] text-red-700 flex-1 min-w-[180px]">{tr('Webhook ir automātiski apturēts pēc atkārtotām neveiksmēm. Notikumi netiek sūtīti.', 'Webhook auto-paused after repeated failures. Events are not being sent.')}</span>
                    <Button type="button" size="sm" onClick={reactivateWebhook} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reactivate-webhook">{tr('Atkārtoti aktivizēt', 'Reactivate')}</Button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://your-site.com/kolv-webhook"
                    className="flex-1 min-w-[220px] text-xs bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg px-3 py-2" data-testid="webhook-url-input" />
                  <Button type="button" onClick={saveWebhook} className="bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="save-webhook">{tr('Saglabāt', 'Save')}</Button>
                  <Button type="button" variant="outline" onClick={testWebhook} disabled={!webhookUrl} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="test-webhook">{tr('Tests', 'Test')}</Button>
                </div>
                <div className="mt-3" data-testid="webhook-events">
                  <p className="text-[11px] font-medium text-[#121212] mb-1.5">{tr('Notikumi, kurus saņemt:', 'Events to receive:')}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {availableEvents.map((ev) => (
                      <label key={ev} className="flex items-center gap-1.5 text-[11px] text-[#5C5C5C] cursor-pointer select-none">
                        <input type="checkbox" checked={webhookEvents.includes(ev)} onChange={() => toggleEvent(ev)}
                          className="accent-[#4A6B53] w-3.5 h-3.5" data-testid={`webhook-event-${ev}`} />
                        <span className="font-mono">{ev}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1.5">{tr('Neizdevušās piegādes tiek automātiski atkārtotas (1m → 10m → 1h → 6h).', 'Failed deliveries auto-retry (1m → 10m → 1h → 6h).')}</p>
                </div>
                {webhookSecret && <p className="text-[11px] text-[#9CA3AF] mt-2 font-mono break-all" data-testid="webhook-secret">Secret: {webhookSecret}</p>}
                {deliveries.length >= 0 && webhookUrl && (
                  <div className="mt-3" data-testid="webhook-deliveries">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <p className="text-[11px] font-medium text-[#121212]">{tr('Pēdējās piegādes', 'Recent deliveries')}</p>
                      <select value={filterEvent} onChange={(e) => { setFilterEvent(e.target.value); loadDeliveries(e.target.value, filterStatus); }}
                        className="text-[11px] bg-[#FAFAFA] border border-[#E5E4E0] rounded-md px-1.5 py-1" data-testid="filter-event">
                        <option value="">{tr('Visi notikumi', 'All events')}</option>
                        {availableEvents.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                        <option value="webhook.test">webhook.test</option>
                      </select>
                      <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); loadDeliveries(filterEvent, e.target.value); }}
                        className="text-[11px] bg-[#FAFAFA] border border-[#E5E4E0] rounded-md px-1.5 py-1" data-testid="filter-status">
                        <option value="">{tr('Visi statusi', 'All statuses')}</option>
                        <option value="success">{tr('Veiksmīgi', 'Success')}</option>
                        <option value="failed">{tr('Neizdevušies', 'Failed')}</option>
                      </select>
                      <button type="button" onClick={exportDeliveries} className="text-[11px] text-[#4A6B53] underline ml-auto" data-testid="export-deliveries">{tr('Eksportēt CSV', 'Export CSV')}</button>
                    </div>
                    {deliveries.length === 0 ? (
                      <p className="text-[11px] text-[#9CA3AF]" data-testid="no-deliveries">{tr('Nav piegāžu ar šiem filtriem.', 'No deliveries match these filters.')}</p>
                    ) : (
                    <div className="space-y-1">
                      {deliveries.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 text-[11px]">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.success ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="font-mono text-[#5C5C5C] w-28 shrink-0 truncate">{d.event}</span>
                          <span className="text-[#9CA3AF]">{d.status_code || d.error || '—'}</span>
                          {!d.success && <button onClick={() => retryDelivery(d.id)} className="ml-auto text-[#D97736] underline" data-testid={`retry-${d.id}`}>{tr('Atkārtot', 'Retry')}</button>}
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Liquid theme engine (Premium Ultra) */}
          {has('theme_engine') && (
            <Section icon={Code2} title={tr('Tēmu dzinējs', 'Theme engine')} locked={false} tr={tr}>
              <div className="flex items-center gap-2 mb-2" data-testid="theme-lang-selector">
                <span className="text-xs font-medium text-[#121212]">{tr('Dzinējs:', 'Engine:')}</span>
                {[{ id: 'liquid', label: 'Liquid' }, { id: 'jinja2', label: 'Jinja2 (Twig)' }].map((opt) => (
                  <button key={opt.id} type="button"
                    onClick={() => { themeLangRef.current = opt.id; update({ theme_lang: opt.id }); setThemePreview(null); }}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${(c.theme_lang || 'liquid') === opt.id ? 'bg-[#4A6B53] text-white border-[#4A6B53]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'}`}
                    data-testid={`theme-lang-${opt.id}`}>{opt.label}</button>
                ))}
              </div>
              <p className="text-xs text-[#5C5C5C] mb-2">{(c.theme_lang || 'liquid') === 'jinja2'
                ? tr('Veido tēmu ar Jinja2 (Twig sintakse: {{ }}, {% %}, filtri). Sandbox režīms — <script> un bīstamas funkcijas tiek bloķētas. Mainīgie: shop.*, listings[].*, categories[], stats.*.', 'Build a theme with Jinja2 (Twig syntax: {{ }}, {% %}, filters). Sandboxed — scripts/dangerous calls blocked. Variables: shop.*, listings[].*, categories[], stats.*.')
                : tr('Veido pilnu veikala tēmu ar Liquid. Pieejamie mainīgie: shop.name, shop.description, listings[].title, listings[].price_formatted, listings[].image, listings[].url, categories[], listing_count. <script> tiek bloķēts.', 'Build a full shop theme with Liquid. Variables: shop.*, listings[].*, categories[], listing_count. Scripts are blocked.')}</p>
              <div className="mb-3">
                <p className="text-xs font-medium text-[#121212] mb-1.5">{tr('Sākt no parauga:', 'Start from a template:')}</p>
                <div className="flex flex-wrap gap-2" data-testid="theme-gallery">
                  {((c.theme_lang || 'liquid') === 'jinja2' ? JINJA_GALLERY : THEME_GALLERY).map((g) => (
                    <Button key={g.id} type="button" variant="outline" size="sm"
                      onClick={() => { update({ liquid_theme: g.liquid }); setThemePreview(null); }}
                      className="border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736]" data-testid={`gallery-${g.id}`}>
                      {g.name[language] || g.name.lv}
                    </Button>
                  ))}
                </div>
              </div>
              <Textarea value={c.liquid_theme} onChange={(e) => update({ liquid_theme: e.target.value })} rows={10}
                placeholder="<h1>{{ shop.name }}</h1>{% for l in listings %}{{ l.title }}{% endfor %}"
                className="font-mono text-xs bg-[#FAFAFA] border-[#E5E4E0]" data-testid="liquid-theme-input" />
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                <Button type="button" variant="outline" onClick={loadDefaultTheme} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="load-default-theme">{tr('Ielādēt paraugu', 'Load starter')}</Button>
                <Button type="button" onClick={previewTheme} disabled={themeBusy} className="bg-[#4A6B53] hover:bg-[#3d5944] text-white" data-testid="preview-theme">{tr('Priekšskatīt', 'Preview')}</Button>
                <Button type="button" variant="outline" onClick={exportTheme} disabled={!c.liquid_theme} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="export-theme">{tr('Eksportēt', 'Export')}</Button>
                <label className="inline-flex items-center px-3 py-2 text-sm border border-[#E5E4E0] text-[#5C5C5C] rounded-md cursor-pointer hover:border-[#D97736]" data-testid="import-theme-label">
                  {tr('Importēt', 'Import')}
                  <input type="file" accept=".liquid,.txt,.html" onChange={importTheme} className="hidden" data-testid="import-theme-input" />
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs text-[#5C5C5C] cursor-pointer ml-auto select-none" data-testid="live-preview-toggle">
                  <input type="checkbox" checked={livePreview} onChange={(e) => setLivePreview(e.target.checked)} className="w-3.5 h-3.5 accent-[#4A6B53]" />
                  {tr('Reāllaika priekšskatījums', 'Live preview')}
                </label>
              </div>
              {themeError && (
                <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-mono break-all" data-testid="theme-error">{tr('Tēmas kļūda: ', 'Theme error: ')}{themeError}</p>
              )}
              {themePreview != null && (
                <div className="mt-3" data-testid="theme-preview-container">
                  <div className="flex items-center gap-1.5 mb-2" data-testid="preview-device-toggle">
                    {[{ id: 'desktop', Icon: Monitor, label: tr('Dators', 'Desktop') }, { id: 'tablet', Icon: Tablet, label: tr('Planšete', 'Tablet') }, { id: 'phone', Icon: Smartphone, label: tr('Telefons', 'Phone') }].map(({ id, Icon, label }) => (
                      <button key={id} type="button" onClick={() => setPreviewDevice(id)}
                        title={label}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border transition-colors ${previewDevice === id ? 'bg-[#4A6B53] text-white border-[#4A6B53]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'}`}
                        data-testid={`preview-device-${id}`}>
                        <Icon className="w-3.5 h-3.5" />{label}
                      </button>
                    ))}
                    <button type="button" onClick={() => {
                      if (!themePreview) return;
                      const blob = new Blob([themePreview], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank', 'noopener,noreferrer');
                      setTimeout(() => URL.revokeObjectURL(url), 30000);
                    }} className="ml-auto text-xs px-2.5 py-1 rounded-md border border-[#E5E4E0] text-[#5C5C5C] bg-white hover:border-[#D97736] transition-colors" data-testid="fullscreen-preview">
                      {tr('Pilnekrāns ↗', 'Fullscreen ↗')}
                    </button>
                  </div>
                  <div className="flex justify-center bg-[#F3F2EE] rounded-lg p-3 overflow-hidden">
                    <iframe title="theme-preview" sandbox="allow-same-origin" srcDoc={themePreview}
                      style={{ width: previewDevice === 'phone' ? 390 : previewDevice === 'tablet' ? 768 : '100%', maxWidth: '100%' }}
                      className="h-80 rounded-lg border border-[#E5E4E0] bg-white transition-all duration-300" data-testid="theme-preview-frame" />
                  </div>
                </div>
              )}
              {user?._id && c.liquid_theme && (
                <a href={`/shop/${user._id}/theme`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-[#D97736] underline" data-testid="view-themed-shop-link">{tr('Skatīt manu tēmu tiešsaistē →', 'View my live theme →')}</a>
              )}
              <label className="flex items-center gap-2 text-sm text-[#121212] cursor-pointer mt-3" data-testid="theme-default-toggle">
                <input type="checkbox" checked={!!c.theme_default} onChange={(e) => update({ theme_default: e.target.checked })} className="w-4 h-4 accent-[#4A6B53]" />
                {tr('Lietot šo tēmu kā noklusējuma veikala skatu', 'Use this theme as the default shop view')}
              </label>
              {versions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#F3F2EE]" data-testid="theme-versions">
                  <p className="text-xs font-medium text-[#121212] mb-2">{tr('Versiju vēsture', 'Version history')}</p>
                  <div className="space-y-1.5">
                    {versions.slice().reverse().map((v) => (
                      <div key={v.index} className="flex items-center gap-2 text-xs">
                        <span className="text-[#9CA3AF] w-32 shrink-0">{v.saved_at ? new Date(v.saved_at).toLocaleString('lv-LV') : `#${v.index}`}</span>
                        <code className="flex-1 truncate text-[#5C5C5C] bg-[#FAFAFA] px-2 py-1 rounded">{v.preview}</code>
                        <Button type="button" size="sm" variant="outline" onClick={() => restoreVersion(v.index)} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid={`restore-version-${v.index}`}>{tr('Atjaunot', 'Restore')}</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Live preview */}
          <div id="kolv-shop" className="rounded-xl border border-dashed border-[#E5E4E0] overflow-hidden" data-testid="customizer-preview" style={{ backgroundColor: c.bg_color, color: c.text_color, fontFamily: fontStyle }}>
            <style>{c.custom_css}</style>
            <div className="text-xs text-[#5C5C5C] px-3 pt-3">{tr('Priekšskatījums', 'Preview')}</div>
            {has('banners') && c.banner_top_html && <div className="px-3" dangerouslySetInnerHTML={{ __html: c.banner_top_html }} />}
            <div className="p-3">
              <div className="h-16 rounded-lg mb-3" style={{ background: `linear-gradient(135deg, ${c.primary_color}, ${c.primary_color}dd)` }} />
              <h4 className="text-lg font-bold mb-1" style={{ color: c.text_color }}>{tr('Veikala nosaukums', 'Shop Name')}</h4>
              <button className="product-card cta-button px-4 py-1.5 rounded-full text-white text-sm" style={{ backgroundColor: c.secondary_color }}>{tr('Poga', 'Button')}</button>
            </div>
            {has('banners') && c.banner_bottom_html && <div className="px-3 pb-3" dangerouslySetInnerHTML={{ __html: c.banner_bottom_html }} />}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {has('preview_undo') ? (
              <>
                <Button onClick={handleSaveDraft} disabled={saving} variant="outline" className="border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white" data-testid="save-draft-btn">
                  <Save className="w-4 h-4 mr-2" /> {tr('Saglabāt melnrakstu', 'Save draft')}
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="publish-btn">
                  {success ? <><Check className="w-4 h-4 mr-2" /> {tr('Publicēts!', 'Published!')}</> : <>{tr('Publicēt veikalā', 'Publish to shop')}</>}
                </Button>
              </>
            ) : (
              <Button onClick={handleSave} disabled={saving} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="save-customization">
                {success ? <><Check className="w-4 h-4 mr-2" /> {tr('Saglabāts!', 'Saved!')}</> : <><Save className="w-4 h-4 mr-2" /> {tr('Saglabāt dizainu', 'Save Design')}</>}
              </Button>
            )}
            {has('preview_undo') && (
              <>
                <Button variant="outline" onClick={undo} disabled={!history.length} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="undo-btn"><Undo2 className="w-4 h-4 mr-1" /> {tr('Atsaukt', 'Undo')}</Button>
                <Button variant="outline" onClick={resetDefaults} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> {tr('Atjaunot noklusējumu', 'Reset')}</Button>
              </>
            )}
            {hasDraft && has('preview_undo') && (
              <Badge className="bg-amber-100 text-amber-700 border-0" data-testid="draft-indicator">{tr('Nepublicēts melnraksts', 'Unpublished draft')}</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, locked, children, tr }) {
  return (
    <div className={locked ? 'opacity-60' : ''}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#4A6B53]" />
        <h4 className="font-medium text-sm text-[#121212]">{title}</h4>
        {locked && <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0 text-[10px]"><Lock className="w-2.5 h-2.5 mr-1" /> {tr('Augstāks plāns', 'Higher plan')}</Badge>}
      </div>
      {children}
    </div>
  );
}
